import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NoticeDefinition, OverlayService } from './overlay.service';
import { ReduxService } from './redux.service';
import { LocalStorageService } from 'angular-2-local-storage';
import { DateRange } from './../interfaces/dateRange';
import { SapiService, SapiEntity } from './sapi.service';
import { Observable, BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import {
	Project, ProjectFlowchart, ProjectStatsReference, ProjectStatsConfiguration,
	ProjectStep, ProjectChapter, FlowchartGridRules, ProjectStepWithPositions, ProjectPosition, ProjectPerformanceStats
} from './../interfaces/project';
import { Injectable } from '@angular/core';
import { LocationService } from './location.service';
import { Mediaclip } from 'app/interfaces/mediaclip';
import { FlowchartStep } from 'app/shared/models/flowchart-step.model';
import { FilterConfig } from './filter-set.service';

@Injectable()
export class ProjectService {
	public projectContext: string;
	get projectContextWithNone(): string {
		return this.projectContext || '__none__';
	};
	set projectContextWithNone(c: string) {
		this.projectContext = c === '__none__' ? null : c;
	};
	public projectWithStatsObs: Observable<Project>;
	public statsObs: Observable<any>;
	public statsRefSubject: BehaviorSubject<any>;
	public projectWithStatsSubject: ReplaySubject<Project>;
	public statsLoading: BehaviorSubject<boolean>;
	public statsCollectionStartDate: Date;
	public projectDownloadUrl: string;
	public mainMediaClipSubject: ReplaySubject<Mediaclip>;
	public statsDateRangeSubject: ReplaySubject<DateRange> = new ReplaySubject<DateRange>(1);

	previousPerformanceParams: any;
	performanceStatsData: ProjectPerformanceStats;
	performanceStatsDataSubject = new ReplaySubject<ProjectPerformanceStats>(1);

	private _projectStats: Project;
	private statsRef: ProjectStatsReference;
	private project: Project;
	private lightProject: Project;
	private statsDateConfig: any;
	private statsConfiguration: ProjectStatsConfiguration;
	private mainMediaClip: Mediaclip;
	private lightProjectObs: Observable<Project>;

	private projectStatsSubscription = new ReplaySubject<Project>(1);
	private projectId: string;
	private statsMode: boolean;
	private projectPerformanceLoading = false;

	get projectStats(): any {
		return this._projectStats;
	}

	constructor(
		private sapiService: SapiService,
		private localStorageService: LocalStorageService,
		protected reduxService: ReduxService,
		private locationService: LocationService,
		private translate: TranslateService,
		private overlayService: OverlayService) {
		this.statsRefSubject = new BehaviorSubject({});
		this.statsLoading = new BehaviorSubject(false);
		this.statsConfiguration = {};
		this.statsCollectionStartDate = new Date('2017-06-19');
		this.mainMediaClipSubject = new ReplaySubject<Mediaclip>(1);

		this.projectDownloadUrl = this.locationService.server + '/sapi/project/';

		this.projectWithStatsSubject = new ReplaySubject<Project>(1);

		this.loadConfig();
	}

	getLightProject(projectId: string) {
		this.projectId = projectId;
		const extraOptions = {
			params: {
				includeContent: 'false'
			}
		};
		this.lightProjectObs = this.sapiService.get('project', this.projectId, '', extraOptions).pipe(
			map((project: Project) => {
				this.lightProject = project;
				if (this.projectStatsSubscription && !this.project) {
					this.projectStatsSubscription.next(this.lightProject);
				}
				return project;
			}));
		return this.lightProjectObs;
	}

	get(projectId: string) {
		this.projectId = projectId;
		if (this.project || this.lightProject) {
			this.projectStatsSubscription.next(this.project || this.lightProject);
		}
		return this.projectStatsSubscription;
	}

	setStatsDateRange(dateConf?: DateRange) {
		if (!this.statsConfiguration) {
			this.statsConfiguration = {};
		}
		if (dateConf) {
			this.statsConfiguration.statsDateStart = dateConf.startDate;
			this.statsConfiguration.statsDateEnd = dateConf.endDate;
			this.statsDateRangeSubject.next(dateConf);
		}
	}

	getMonthsInStatsDateRange() {
		if (!this.statsConfiguration || !this.statsConfiguration.statsDateStart || !this.statsConfiguration.statsDateEnd) {
			return 0;
		} else {
			const startDate = this.statsConfiguration.statsDateStart;
			const endDate = this.statsConfiguration.statsDateEnd;

			return 1 + endDate.getMonth() - startDate.getMonth() + (12 * (endDate.getFullYear() - startDate.getFullYear()));
		}
	}

	setMainMediaClipId(id: string) {
		if (this.project) {
			this.project.mainMediaClipId = id;
		}
		if (this.mainMediaClip && this.mainMediaClip.id === id) {
			return;
		}
		if (!id) {
			this.mainMediaClipSubject.next(null);
		} else {
			this.getMainMediaClip(id);
		}
	}

	setStatsMode(statsMode: boolean) {
		this.statsMode = statsMode;
	}

	getStepWidth(generateFlowchart: boolean = false) {
		// Directly links to flowchart-step.component.scss :host -> width - 2px
		// So for example :host -> .stats-mode -> width = 200px, and this refers to 212 in the step width for stats
		return (this.statsMode && !generateFlowchart ? 212 : 137);
	}

	getProjectStats(dateConf?: DateRange, projectId?: string): Observable<any> {
		let dateConfig = this.statsDateConfig || {};
		const id = projectId || this.projectId;

		if (dateConf) {
			dateConfig.statsDateStart = dateConf.startDate;
			dateConfig.statsDateEnd = dateConf.endDate;
		}

		if (!dateConfig || !dateConfig.statsDateStart) {
			this.statsDateConfig = dateConfig = {};
			dateConfig.statsDateStart = new Date();
			dateConfig.statsDateStart.setHours(0);
			dateConfig.statsDateStart.setMinutes(0);
			dateConfig.statsDateStart.setSeconds(0);
			dateConfig.statsDateStart.setMilliseconds(0);
			dateConfig.statsDateStart.setDate(dateConfig.statsDateStart.getDate() - 31);
			const endDate = new Date();
			endDate.setDate(endDate.getDate() - 1);
			endDate.setHours(23);
			endDate.setMinutes(59);
			endDate.setSeconds(59);
			endDate.setMilliseconds(999);
			dateConfig.statsDateEnd = endDate;
		}
		if (!this.statsDateConfig) {
			this.statsDateConfig = dateConfig;
		}
		this.statsConfiguration = this.statsDateConfig;

		const extraOptions: any = {
			params: {
				fromDate: dateConfig.statsDateStart.toISOString(),
				toDate: dateConfig.statsDateEnd.toISOString()
			}
		};
		if (this.projectContext) {
			extraOptions.params.context = this.projectContext;
		}
		this.statsLoading.next(true);

		const obs = this.sapiService.get('project', id, 'stats', extraOptions);
		obs.subscribe((res: SapiEntity) => {
			const ps = res as Project;
			if (this.project && this.project.flowchart && !ps.flowchart) {
				ps.flowchart = this.project.flowchart;
			}
			this.project = ps; // ?? check if needed
			this.projectWithStatsSubject.next(ps);
			this.projectStatsSubscription.next(ps);
			this.makeStatsRef(ps);
			this.statsLoading.next(false);
			return ps;
		});
		return obs;
	}

	getProjectPerformanceStatsData(params: any): Observable<any> {
		if (!this.project?.id) {
			return;
		}
		this.previousPerformanceParams = params;
		const loadId = 'ProjectStatsPerformanceComponent:getPerformanceStatsData';
		this.overlayService.increaseLoadCount(loadId);
		return this.sapiService.getUrl(`/project/${this.project.id}/performance`, params).pipe(map((result: any) => {
			this.overlayService.decreaseLoadCount(loadId);
			this.performanceStatsData = result;
			return result;
		}));
	}

	setProjectPerformanceStatsData(params: any): void {
		if (this.previousPerformanceParams && this.projectPerformanceLoading) {
			if (this.performanceStatsData) {
				this.performanceStatsDataSubject.next(this.performanceStatsData);
			}
			return;
		}

		this.previousPerformanceParams = params;
		const loadId = 'ProjectStatsPerformanceComponent:getPerformanceStatsData';
		this.overlayService.increaseLoadCount(loadId);
		this.projectPerformanceLoading = true;

		this.sapiService.getUrl(`/project/${this.project.id}/performance`, params).subscribe((result: ProjectPerformanceStats) => {
			this.projectPerformanceLoading = false;
			this.overlayService.decreaseLoadCount(loadId);
			this.performanceStatsData = result;
			this.performanceStatsDataSubject.next(result);
		});
	}

	reset() {
		delete this.mainMediaClip;
		this.project = null;
		this.projectContext = null;
		this.lightProject = null;
		this.statsRef = null;
		this.statsRefSubject.next(null);
		this.mainMediaClipSubject.next(null);
		this.projectWithStatsSubject.next(null);
	}

	setProject(project: Project) {
		if (!this.project || this.project.id !== project.id) {
			this.reset();
		}
		this.project = project;
		this.lightProject = project;
		if (!this.projectWithStatsSubject) {
			this.projectWithStatsSubject = new ReplaySubject<Project>(1);
		}
		// this.projectWithStatsSubject.next(this.project);
		this.checkProjectPublishedDate(project);
		this.setMainMediaClipId(this.project.mainMediaClipId);
	}

	setContext(context) {
		this.projectContext = context;
	}

	getFilterConfigOptions(): FilterConfig[] {
		return [
			{
				property: 'title',
				label: this.translate.instant('TITLE'),
				type: 'text'
			}, {
				property: 'deeplink',
				label: this.translate.instant('DEEP_LINK'),
				type: 'text'
			}, {
				property: 'cat',
				label: this.translate.instant('TAGS'),
				type: 'multiSelect'
			}, {
				property: 'status',
				label: this.translate.instant('STATUS'),
				type: 'select'
			}, {
				property: 'createdBy',
				label: this.translate.instant('CREATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'updatedBy',
				label: this.translate.instant('UPDATED_BY'),
				type: 'multiSelect'
			}, {
				property: 'publisheddate',
				label: this.translate.instant('PUBLISHED_DATE'),
				type: 'date'
			}, {
				property: 'createddate',
				label: this.translate.instant('CREATED_DATE'),
				type: 'date'
			}, {
				property: 'updateddate',
				label: this.translate.instant('UPDATED_DATE'),
				type: 'date'
			}, {
				property: 'hasInteractivity',
				label: this.translate.instant('HAS_INTERACTIVITY'),
				type: 'boolean'
			}
		];
	}

	getProject(): Project {
		return this.project || this.lightProject;
	}

	getStatsQueryParamsString(): string {
		if (this.statsConfiguration.statsDateStart && this.statsConfiguration.statsDateEnd) {
			const context = this.projectContext ? '&context=' + this.projectContext : '';
			return 'fromDate=' + this.statsConfiguration.statsDateStart.toISOString() +
				'&toDate=' + this.statsConfiguration.statsDateEnd.toISOString() + context;
		}
		return '';
	}

	getStatsConfiguration(): ProjectStatsConfiguration {
		return this.statsConfiguration;
	}

	loadConfig() {

		const configJson = this.localStorageService.get('project_stats_config');

		let config;

		if (configJson) {
			config = JSON.parse(configJson as string);
		} else {
			// defaults
			config = {
				showEngagement: true,
				showKpis: true,
				showNavigation: false
			};
		}

		this.statsConfiguration = config as ProjectStatsConfiguration;
	}

	checkProjectPublishedDate(project: Project) {
		if (project && project.publisheddate) {
			const projectPublishedDate = new Date(project.publisheddate);
			if (this.statsConfiguration.statsDateStart < projectPublishedDate) {
				this.statsCollectionStartDate = projectPublishedDate;
				this.statsConfiguration.statsDateStart = projectPublishedDate;
				// this.storeConfig();
			}
		}
	}

	findStep(id: string, project?: any): ProjectStep {
		if (!project) {
			project = this.project;
		}
		let flowchartStep: ProjectStep = {};
		if (project && (project.steps || project.chapters)) {
			if (project.chapters) {
				const chapterLength = project.chapters.length;
				for (let i = 0; i < chapterLength; i++) {
					const chapter = project.chapters[i];
					chapter.steps.forEach((step: ProjectStep) => {
						if (step.entityId === id) {
							flowchartStep = step;
							// flowchartStep.description = step.description;
						}
					});
				}
			} else if (project.steps && project.steps.length > 0 && project.steps[0].positions) {
				if (project && project.steps) {
					project.steps.forEach((step: ProjectStepWithPositions) => {
						if (step && step.positions) {
							step.positions.forEach((position: ProjectPosition) => {
								if (position.mediaClipId === id) {
									flowchartStep = position;
									// flowchartStep.description = position.description;
								}
							});
						}
					});
				}
			}
		}
		return flowchartStep;
	}

	downloadFormData() {
		this.sapiService.download(
			'project',
			this.project.id,
			'formdata?' + this.getStatsQueryParamsString()
		).pipe(
			catchError((err: any) => {
				const notice: NoticeDefinition = {
					icon: 'warning',
					title: this.translate.instant('DOWNLOAD_FORM_DATA_FAILED'),
					contents: this.translate.instant('DOWNLOAD_FORM_DATA_FAILED_MESSAGE')
				};

				this.overlayService.createNotice(notice);
				return err;
			})
		).subscribe();
	}

	makeStatsRef(projectWithStats: Project) {
		this.statsRef = {
			mediaclip: {
				viewsIn: {},
				viewsOut: {},
				inits: {},
				views: {}
			},
			project: {
				inits: null,
				views: null,
				visitors: null
			},
			widget: {
				shows: {},
				clicks: {}
			},
			link: {},
			maxLinkCount: 1,
			maxViewCount: 1
		};
		if (projectWithStats && projectWithStats.chapters) {
			const chapterLength = projectWithStats.chapters.length;
			for (let i = 0; i < chapterLength; i++) {
				const chapter = projectWithStats.chapters[i];
				chapter.steps.forEach((step: ProjectStep, stepIndex: number) => {
					let totalOut = 0;
					if (step.stats && step.stats.loadedClipsByCount) {
						step.stats.loadedClipsByCount.forEach((a: any) => {
							totalOut += a.count;
							const c = Number(a.count);
							if (c > this.statsRef.maxLinkCount) {
								this.statsRef.maxLinkCount = c;
							}
							this.statsRef.link[step.entityId + '_TO_' + a.id] = a.count;
						});

						// dropoff:
						this.statsRef.link[step.entityId + '_TO_DROPOFF'] = step.stats.views - totalOut;
					}
					if (step.entityId) {
						this.statsRef.mediaclip.viewsOut[step.entityId] = totalOut;
					}
					if (step.entityId && step.stats && typeof step.stats.views !== 'undefined') {
						this.statsRef.mediaclip.views[step.entityId] = step.stats.views;
						if (step.stats.views > this.statsRef.maxViewCount && step.stats) {
							this.statsRef.maxViewCount = step.stats.views;
						}
					}
					if (step.entityId && step.stats && typeof step.stats.inits !== 'undefined') {
						this.statsRef.mediaclip.inits[step.entityId] = step.stats.inits;
						if (this.project.mainMediaClipId && this.project.mainMediaClipId === step.entityId && step.stats) {
							this.statsRef.project.inits = step.stats.inits;
						}
					}
					if (step.entityId && step.stats && typeof step.stats.visitors !== 'undefined') {
						this.statsRef.project.visitors[step.entityId] = step.stats.visitors;
						if (this.project.mainMediaClipId && this.project.mainMediaClipId === step.entityId && step.stats) {
							this.statsRef.project.visitors = step.stats.visitors;
						}
					}
					if (step.widgets && step.widgets.length) {
						step.widgets.forEach((widget: any) => {
							if (widget.stats && step.entityId) {
								this.statsRef.widget.shows[widget.id + '_' + step.entityId] = widget.stats.shows;
								this.statsRef.widget.clicks[widget.id + '_' + step.entityId] = widget.stats.clicks;
							}
							if (widget.widgets && widget.widgets.length) {
								widget.widgets.forEach((subwidget: any) => {
									if (subwidget.stats) {
										this.statsRef.widget.shows[subwidget.id + '_' + step.entityId] = subwidget.stats.shows;
										this.statsRef.widget.clicks[subwidget.id + '_' + step.entityId] = subwidget.stats.clicks;
									}
								});
							}
						});
						if (step.stats) {
							this.statsRef.mediaclip.inits[step.entityId] = step.stats.inits;
						}
					}
				});
			}
		}
		this.statsRefSubject.next(this.statsRef);
	}

	getOldProjectAndGenerate(gridRules: FlowchartGridRules): ReplaySubject<ProjectFlowchart> {
		const generatedFlowchart = new ReplaySubject<ProjectFlowchart>();
		if (!this.project) {
			this.sapiService.get('project', this.project.id).subscribe((project: Project) => {
				this.project = project;
				generatedFlowchart.next(
					this.autoGenerateFlowchart(project, gridRules)
				);
			}, (err: any) => {
				const notice: NoticeDefinition = {
					icon: 'warning',
					title: this.translate.instant('FAILED_GENERATING_FLOWCHART'),
					contents: this.translate.instant('FAILED_GENERATING_FLOWCHART_MESSAGE')
				};

				this.overlayService.createNotice(notice);
			});
		} else {
			generatedFlowchart.next(
				this.autoGenerateFlowchart(this.project, gridRules)
			);
		}
		return generatedFlowchart;
	}

	generateId() {
		return 'c' + Math.random().toString(36).substr(2, 16);
	}

	positionBasedOnGrid(flowchart: ProjectFlowchart, grid: FlowchartGridRules) {
		for (const stepKey in flowchart.steps) {
			if (flowchart.steps.hasOwnProperty(stepKey)) {
				const step = flowchart.steps[stepKey];
				if (step.col || step.col === 0) {
					step.x = (step.col * (grid.x.gutter + grid.x.track)) + (grid.x.gutter / 2);
				}
				if (step.row || step.row === 0) {
					step.y = (step.row * (grid.y.gutter + grid.y.track)) + (grid.y.gutter / 2);
				}
			}
		}
		return flowchart;
	}

	autoGenerateFlowchart(project: Project, gridRules: FlowchartGridRules): ProjectFlowchart {
		// console.warn('autoGenerateFlowchart project', project, 'withStats', this.statsMode);
		const flowchart: ProjectFlowchart = { steps: {}, fromSteps: true };
		let highestPositionCount = 1;
		// let stepWidth: number = this.getStepWidth(true);

		// This will determine the distance between steps
		// let offsetX = stepWidth * 3;
		// let offsetY = 620;
		// let rowOffset = 5;
		if (!project.chapters) {
			return null;
		}

		project.chapters.forEach((chapter: ProjectChapter) => {
			// set highestPositionCount to vertically center steps
			if (chapter.steps && chapter.steps.length > highestPositionCount) {
				highestPositionCount = chapter.steps.length;
			}
			if (!chapter.id) {
				chapter.id = this.generateId();
			}
		});

		// project.chapters.forEach((chapter: ProjectChapter, chapterIndex: number) => {
		// 	if (!chapter.id) {
		// 		chapter.id = this.generateId();
		// 	}
		// 	// vertically center steps:
		// 	let startOffsetY = 40 + ((highestPositionCount / 2 - chapter.steps.length / 2) * offsetY);
		// 	let rowOffsetStart = rowOffset + ((highestPositionCount / 2 - chapter.steps.length / 2) * rowOffset);
		// 	let chapterTitle = chapter.title;
		// 	chapter.steps.forEach((step: any, stepIndex: number) => {
		// 		let stepId = 'id-p' + project.id + 'c' + step.entityId;

		// 		if (step.entityId && ! flowchart.steps[stepId]) {
		// 			let isStart = step.entityId === project.mainMediaClipId;
		// 			let n = flowchart.steps[stepId] = {
		// 				isStart: isStart,
		// 				id: stepId,
		// 				entityType: 'mediaclip',
		// 				entityId: step.entityId,
		// 				description: step.title,
		// 				x: 50 + (chapterIndex * offsetX),
		// 				y: startOffsetY + (stepIndex * offsetY),
		// 				col: chapterIndex,
		// 				row: rowOffsetStart + (stepIndex * rowOffset),
		// 				width: stepWidth,
		// 				height: (isStart ? 85 : 60), // for stats 75 + ?
		// 				stepsIn: [] as any,
		// 				stepsOut: [] as any,
		// 				stepTitle: chapterTitle,
		// 				stepIndex: chapterIndex
		// 			};
		// 		}
		// 	});
		// });
		flowchart.chapters = [];
		project.chapters.forEach((chapter: ProjectChapter, chapterIndex: number) => {
			const fc = { ...chapter };
			if (!fc.id) {
				fc.id = this.generateId();
			}
			delete fc.steps;
			flowchart.chapters.push(fc);
			chapter.steps.forEach((step: any, stepIndex: number) => {
				const stepId = 'id-p' + project.id + 'c' + step.entityId;
				const n = flowchart.steps[stepId];
				if (!n) {
					return;
				}
				n.chapterId = chapter.id;

				if (step.loadedClipIds) {
					step.loadedClipIds.forEach((clipId: string) => {
						const toStep = flowchart.steps['id-p' + project.id + 'c' + clipId];
						if (step.entityId !== clipId
							&& toStep && toStep.stepIndex > n.stepIndex
						) {
							n.stepsOut.push('id-p' + project.id + 'c' + clipId);
						}
					});
				}
				if (step.loadedByClipIds) {
					const loadedClipLength = step.loadedByClipIds.length;
					for (let i = 0; i < loadedClipLength; i++) {
						const clipId = step.loadedByClipIds[i];
						const fromStep = flowchart.steps['id-p' + project.id + 'c' + clipId];
						if (step.entityId !== clipId
							&& fromStep && fromStep.stepIndex < n.stepIndex
						) {
							n.stepsIn.push('id-p' + project.id + 'c' + clipId);
						}
					}
				}
			});
		});
		project.flowchart = this.positionBasedOnGrid(flowchart, gridRules);

		// console.warn('project', project);
		return project.flowchart;
	}

	rebuildFlowchart() {
		const extraOptions = {
			params: { rebuildFlowchart: true }
		};
		this.statsLoading.next(true);

		this.sapiService.get('project', this.project.id, null, extraOptions).subscribe(((res: SapiEntity) => {
			this.project = res as Project;

			this.fixStepDimensions(this.project);

			this.projectStatsSubscription.next(this.project);
			this.reduxService.dispatch({ type: 'FROM_GENERATION', loadData: { data: this.project.flowchart } });
			this.statsLoading.next(false);
		}));
	}

	fixStepDimensions(project: Project) {
		const flowchart: ProjectFlowchart = project.flowchart;
		const chapters: ProjectChapter[] = project.chapters;
		const stepWidth: number = this.getStepWidth(true);
		if (flowchart && flowchart.steps) {
			for (const property in flowchart.steps) {
				if (flowchart.steps.hasOwnProperty(property)) {
					const step: FlowchartStep = flowchart.steps[property];
					if (step) {
						step.width = stepWidth;
						step.height = 60;

						const filteredChapters = chapters.filter((projectChapter: any) => projectChapter.id === step.chapterId);
						const chapter = filteredChapters[0];
						if (!step.x) {
							const chapterIndex = chapters.indexOf(chapter);
							if (chapterIndex >= 0) {
								step.x = 100 + (335 * chapterIndex);
							}
						}
						if (chapter && chapter.steps) {
							for (let stepIndex = 0; stepIndex < chapter.steps.length; stepIndex++) {
								const chapterStep = chapter.steps[stepIndex] as any;
								if (chapterStep.id === step.id) {
									if (!step.y) {
										step.y = 120 + (stepIndex * 180);
									}
									chapterStep.x = step.x;
									chapterStep.y = step.y;
									chapterStep.width = step.width;
									chapterStep.height = step.height;
								}
							}
						}
					}
				}
			}
		}
	}

	private getMainMediaClip(id: string) {
		this.sapiService.get('commonclip', id).subscribe((mediaClip: Mediaclip) => {
			this.mainMediaClip = mediaClip;
			this.mainMediaClipSubject.next(mediaClip);
		});
	}

}
